import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Tabs } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const TeacherLogin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // 登录
  const handleLogin = async (values: any) => {
    setLoading(true);
    try {
      const response = await axios.post('/api/teachers/login', {
        username: values.username,
        password: values.password
      });

      if (response.data.success) {
        // 保存token和教师信息到localStorage
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('teacher', JSON.stringify(response.data.teacher));
        message.success('Login successful!');
        
        // 根据角色跳转到不同页面
        if (response.data.teacher.role === 'super_admin') {
          navigate('/super-admin');
        } else {
          navigate('/teacher');
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Login failed. Please check your username and password.';
      message.error(errorMessage);
      if (errorMessage.includes('Invalid username or password')) {
        message.info('Tip: If this is a new account, please register first. Default account: admin / admin123');
      }
    } finally {
      setLoading(false);
    }
  };

  // 注册
  const handleRegister = async (values: any) => {
    setLoading(true);
    try {
      const response = await axios.post('/api/teachers/register', {
        username: values.username,
        password: values.password,
        name: values.name,
        email: values.email
      });

      if (response.data.success) {
        message.success('Registration successful! Please login');
        form.setFieldsValue({ username: values.username, password: '' });
          setActiveTab('login');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const tabItems = [
    {
      key: 'login',
      label: 'Login',
      children: (
        <Form
          form={form}
          name="login"
          onFinish={handleLogin}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Please enter username' }]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Enter username" 
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please enter password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter password"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              size="large"
            >
              Login
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'register',
      label: 'Register',
      children: (
        <Form
          name="register"
          onFinish={handleRegister}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please enter username' },
              { min: 3, message: 'Username must be at least 3 characters' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Enter username" 
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter password"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter password again"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Name (Optional)"
          >
            <Input 
              placeholder="Enter name" 
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email (Optional)"
            rules={[
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input 
              placeholder="Enter email" 
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              size="large"
            >
              Register
            </Button>
          </Form.Item>
        </Form>
      )
    }
  ];

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: '450px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}
        title={
          <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold' }}>
            👨‍🏫 Teacher Login
          </div>
        }
      >
        <Tabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} />
      </Card>
    </div>
  );
};

export default TeacherLogin;

